import { supportService } from '@/services/support'
import type { SupportMessage, SupportMessageInput } from '@/types/support'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { supportKeys } from './query-keys'
import type { SupportMessageUpdate } from './types'

// Create support message mutation
export const useCreateSupportMessage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: SupportMessageInput) => supportService.createSupportMessage(data),
    onSuccess: (messageId, variables) => {
      // Invalidate all support lists to refresh data
      queryClient.invalidateQueries({ queryKey: supportKeys.lists() })
      queryClient.invalidateQueries({ queryKey: supportKeys.userMessages(variables.userId) })
      toast.success('Support message sent successfully')
    },
    onError: (error) => {
      console.error('Error creating support message:', error)
      toast.error('Failed to send support message. Please try again.')
    },
  })
}

// Update support message status with optimistic updates
export const useUpdateSupportMessageStatus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      messageId,
      status,
      adminNotes,
      resolvedBy,
    }: {
      messageId: string
      status: SupportMessage['status']
      adminNotes?: string
      resolvedBy?: string
    }) => supportService.updateSupportMessageStatus(messageId, status, adminNotes, resolvedBy),

    onMutate: async ({ messageId, status, adminNotes }) => {
      await queryClient.cancelQueries({ queryKey: supportKeys.detail(messageId) })

      const previousData = queryClient.getQueryData<SupportMessage | null>(supportKeys.detail(messageId))

      // Optimistic update
      if (previousData) {
        queryClient.setQueryData(supportKeys.detail(messageId), {
          ...previousData,
          status,
          adminNotes: adminNotes || previousData.adminNotes,
          updatedAt: new Date() as any,
        })
      }

      return { previousData, messageId }
    },

    onSuccess: (_, { messageId, status }) => {
      queryClient.invalidateQueries({ queryKey: supportKeys.lists() })
      queryClient.invalidateQueries({ queryKey: supportKeys.detail(messageId) })
      toast.success(`Support message status updated to ${status}`)
    },

    onError: (error, { messageId }, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(supportKeys.detail(messageId), context.previousData)
      }
      console.error('Error updating support message status:', error)
      toast.error('Failed to update support message status. Please try again.')
    },
  })
}

// Add admin notes mutation
export const useAddAdminNotes = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ messageId, notes }: { messageId: string; notes: string }) =>
      supportService.addAdminNotes(messageId, notes),

    onMutate: async ({ messageId, notes }) => {
      await queryClient.cancelQueries({ queryKey: supportKeys.detail(messageId) })

      const previousData = queryClient.getQueryData<SupportMessage | null>(supportKeys.detail(messageId))

      // Optimistic update
      if (previousData) {
        queryClient.setQueryData(supportKeys.detail(messageId), {
          ...previousData,
          adminNotes: notes,
          updatedAt: new Date() as any,
        })
      }

      return { previousData, messageId }
    },

    onSuccess: (_, { messageId }) => {
      queryClient.invalidateQueries({ queryKey: supportKeys.detail(messageId) })
      toast.success('Admin notes added successfully')
    },

    onError: (error, { messageId }, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(supportKeys.detail(messageId), context.previousData)
      }
      console.error('Error adding admin notes:', error)
      toast.error('Failed to add admin notes. Please try again.')
    },
  })
}

// Delete support message mutation (if needed)
export const useDeleteSupportMessage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (messageId: string) => {
      // This would need to be implemented in the service
      throw new Error('Delete support message not implemented')
    },

    onMutate: async (messageId) => {
      await queryClient.cancelQueries({ queryKey: supportKeys.detail(messageId) })
      const previousData = queryClient.getQueryData<SupportMessage | null>(supportKeys.detail(messageId))

      // Remove from cache optimistically
      queryClient.removeQueries({ queryKey: supportKeys.detail(messageId) })

      return { previousData, messageId }
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supportKeys.lists() })
      toast.success('Support message deleted successfully')
    },

    onError: (error, messageId, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(supportKeys.detail(messageId), context.previousData)
      }
      console.error('Error deleting support message:', error)
      toast.error('Failed to delete support message. Please try again.')
    },
  })
}
